interface CleanOptions {
  /** Largest size kept, in pixels */
  maxW: number;
  maxH: number;
  /** True for paper: these pixels become transparent */
  isBackground: (r: number, g: number, b: number) => boolean;
  /** Multiplies the ink colour (below 1 = darker) */
  darken: number;
}

/** Signature: printed about 130 × 34 pt. Anything light is paper; ink is darkened for a crisp print. */
const SIGNATURE: CleanOptions = { maxW: 600, maxH: 200, isBackground: (r, g, b) => Math.max(r, g, b) > 205, darken: 0.6 };

/**
 * Stamp: printed about 64 pt square. Only near-white, colourless pixels are paper, so pale blue or red
 * stamp ink is kept, and its colour is left as it is.
 */
const STAMP: CleanOptions = {
  maxW: 400,
  maxH: 400,
  isBackground: (r, g, b) => Math.min(r, g, b) > 195 && Math.max(r, g, b) - Math.min(r, g, b) < 40,
  darken: 0.92,
};

/**
 * Cleans a signature for printing: makes the light background transparent, trims the
 * empty border and scales it down. Returns a PNG data URL, or null when nothing is drawn.
 */
export const cleanSignature = (source: HTMLCanvasElement | HTMLImageElement) => cleanImage(source, SIGNATURE);

/** Same for a company stamp photo, keeping the stamp's ink colour. */
export const cleanStamp = (source: HTMLCanvasElement | HTMLImageElement) => cleanImage(source, STAMP);

function cleanImage(source: HTMLCanvasElement | HTMLImageElement, opt: CleanOptions): string | null {
  const w = source instanceof HTMLImageElement ? source.naturalWidth : source.width;
  const h = source instanceof HTMLImageElement ? source.naturalHeight : source.height;
  if (!w || !h) return null;

  const work = document.createElement('canvas');
  work.width = w;
  work.height = h;
  const ctx = work.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0);
  const img = ctx.getImageData(0, 0, w, h);
  const px = img.data;

  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = (y * w + x) * 4;
      if (px[i + 3] < 40 || opt.isBackground(px[i], px[i + 1], px[i + 2])) {
        px[i + 3] = 0; // background
        continue;
      }
      // Ink: keep its colour, a little darker for a crisp print.
      px[i] = Math.round(px[i] * opt.darken);
      px[i + 1] = Math.round(px[i + 1] * opt.darken);
      px[i + 2] = Math.round(px[i + 2] * opt.darken);
      px[i + 3] = 255;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;
  ctx.putImageData(img, 0, 0);

  const pad = 6;
  const cropX = Math.max(0, minX - pad);
  const cropY = Math.max(0, minY - pad);
  const cropW = Math.min(w, maxX + pad + 1) - cropX;
  const cropH = Math.min(h, maxY + pad + 1) - cropY;
  const scale = Math.min(1, opt.maxW / cropW, opt.maxH / cropH);
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(cropW * scale));
  out.height = Math.max(1, Math.round(cropH * scale));
  out.getContext('2d')?.drawImage(work, cropX, cropY, cropW, cropH, 0, 0, out.width, out.height);
  return out.toDataURL('image/png');
}

/** Loads a picked file as an image element. */
export function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('This file is not an image the browser can open'));
    };
    img.src = url;
  });
}
